import { getPriceComparison, getCheckoutRefreshDeadline } from './checkoutPresentation';
import { PAYMENT_LOAD_ERROR, withPaymentLoadTimeout } from './paymentLoading';
import type {
  Stripe, StripeCheckoutSession, StripeCheckoutLoadActionsSuccess,
  StripeExpressCheckoutElementConfirmEvent, StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent,
} from '@stripe/stripe-js';

type PaymentCallbacks = {
  update: (session: StripeCheckoutSession, busy: boolean) => void;
  error: (message: string) => void;
  submitted: () => void;
  failed: () => void;
  expired?: () => void;
  complete: (sessionId: string) => void;
};

export function describeCheckout(session: StripeCheckoutSession) {
  const recurring = session.recurring;
  const trial = recurring?.trial;
  const interval = recurring
    ? (recurring.intervalCount === 1 ? recurring.interval : `${recurring.intervalCount} ${recurring.interval}s`)
    : '';
  const next = recurring?.dueNext.total.amount;
  const terms = recurring
    ? `${trial ? `${trial.trialPeriodDays}-day free trial. ` : ''}Next payment: ${next} per ${interval}. Renews automatically until cancelled. Cancel anytime in your account.`
    : '';
  return {
    total: session.total.total.amount,
    terms,
    button: trial && session.total.total.minorUnitsAmount === 0
      ? 'Start free trial' : `Subscribe · ${session.total.total.amount}`,
  };
}

// All payment and promotion operations share one lock. Late results from a
// closed form cannot redirect or update a newer checkout attempt.
export function createPaymentActions(actions: StripeCheckoutLoadActionsSuccess, callbacks: PaymentCallbacks) {
  let disposed = false;
  let busy = false;
  let completed = false;
  const update = () => {
    if (disposed) return;
    const session = actions.getSession();
    if (!completed && session.status.type === 'complete') {
      completed = true;
      callbacks.complete(session.id);
    } else if (!completed && session.status.type === 'expired') {
      completed = true;
      callbacks.expired?.();
    }
    if (!disposed) callbacks.update(session, busy || completed);
  };
  const run = async (payment: boolean, operation: () => Promise<{ type: string; error?: { message: string }; session?: StripeCheckoutSession }>) => {
    if (disposed || busy || completed) return;
    busy = true;
    callbacks.error('');
    update();
    if (disposed || completed) { busy = false; return; }
    if (payment) callbacks.submitted();
    try {
      const result = await operation();
      if (disposed) return;
      if (result.type === 'error') throw new Error(result.error?.message ?? 'Please try again.');
      if (payment && result.session && !completed) {
        completed = true;
        callbacks.complete(result.session.id);
      }
    } catch (error) {
      if (disposed) return;
      callbacks.error(error instanceof Error ? error.message : 'Payment could not complete. Please try again.');
      if (payment) callbacks.failed();
    } finally {
      busy = false;
      update();
    }
  };
  return {
    update,
    confirm: (event?: StripeExpressCheckoutElementConfirmEvent) => run(true, () => actions.confirm({
      // The server sets return_url on the Checkout Session. Stripe rejects a
      // second returnUrl here, including when confirming Apple Pay.
      redirect: 'if_required', ...(event ? { expressCheckoutConfirmEvent: event } : {}),
    })),
    applyPromotionCode: (code: string) => run(false, () => actions.applyPromotionCode(code.trim())),
    destroy: () => { disposed = true; },
  };
}

export function checkoutSecondsRemaining(expiresAt: number, now = Date.now()) {
  return Math.max(0, Math.ceil(expiresAt - now / 1000));
}

export async function mountPaymentElements(options: {
  stripe: Stripe; clientSecret: string; target: HTMLElement;
  canApplyPromotionCode: boolean; isCurrent: () => boolean;
  expiresAt?: number | null; summaryTarget?: HTMLElement | null; expiryTarget?: HTMLElement | null;
  ready: () => void; submitted: () => void; failed: () => void;
  complete: (id: string) => void; fatal: (message: string) => void;
}) {
  const { target, summaryTarget, expiryTarget } = options;
  const sdk = options.stripe.initCheckoutElementsSdk({
    clientSecret: options.clientSecret,
    elementsOptions: {
      fonts: [{ family: 'DM Sans', src: `url(${new URL('/fonts/dm-sans-latin-v17.woff2', window.location.origin).href}) format('woff2')`, weight: '400 800' }],
      appearance: {
        theme: 'flat',
        variables: { colorPrimary: '#ee7a2f', colorBackground: '#ffffff', colorText: '#16233f', colorTextSecondary: '#5a6880', colorDanger: '#b42318', borderRadius: '10px', fontFamily: 'DM Sans, system-ui, sans-serif', fontSizeBase: '16px', spacingUnit: '4px', gridRowSpacing: '16px' },
        rules: {
          '.Input': { border: '1px solid #d8d9df', boxShadow: 'none', padding: '12px' },
          '.Input:focus': { borderColor: '#ee7a2f', boxShadow: '0 0 0 3px #ee7a2f18' },
          '.Label': { fontWeight: '600', color: '#16233f', fontSize: '14px', marginBottom: '8px' },
          '.Tab': { border: '1px solid #dadde1', boxShadow: 'none', backgroundColor: '#ffffff' },
          '.Tab--selected': { borderColor: '#ee7a2f', backgroundColor: '#fff3e8', boxShadow: 'none' },
          '.TabLabel--selected': { color: '#9b4817' },
          '.TabIcon--selected': { fill: '#9b4817' },
          '.Block': { backgroundColor: 'transparent', boxShadow: 'none', border: '0', padding: '0' },
        },
      }, loader: 'auto',
    },
  });
  const loaded = await withPaymentLoadTimeout(sdk.loadActions());
  if (!options.isCurrent() || !target.isConnected) return { destroy() {} };
  if (loaded.type === 'error') throw new Error(loaded.error.message);

  target.classList.add('native-payment');
  target.innerHTML = `
    <div class="native-payment-wallets" data-wallets><div data-express-element></div><span>or</span></div>
    <section class="native-card-form">
      <h2>Card details</h2>
      <form data-payment-form>
        <div data-card-loading>Loading card fields…</div>
        <div data-payment-element></div>
        <p class="native-payment-error" role="alert" data-payment-error></p>
        <button class="pixel-btn block native-payment-submit" type="submit" disabled data-payment-submit><span data-payment-label>Submit secure payment</span></button>
      </form>
    </section>`;
  if (summaryTarget) summaryTarget.innerHTML = `
    <div class="reference-price-row"><span><span data-summary-plan></span><small data-summary-reference></small></span><span data-summary-subtotal></span></div>
    <div class="reference-price-row reference-discount" data-summary-offer-row hidden><span data-summary-offer-label></span><span data-summary-offer></span></div>
    <div class="reference-price-row reference-discount" data-summary-discount-row hidden><span data-summary-discount-label>Discount</span><span data-summary-discount></span></div>
    <div class="reference-price-row" data-summary-tax-row hidden><span>Tax</span><span data-summary-tax></span></div>
    <div class="reference-price-row reference-total"><strong>Total today</strong><strong data-payment-total></strong></div>
    <p class="reference-renewal" data-summary-terms></p>`;
  const el = <T extends HTMLElement = HTMLElement>(selector: string) => target.querySelector<T>(selector)!;
  const submit = el<HTMLButtonElement>('[data-payment-submit]');
  const wallets = el('[data-wallets]');
  let ready = false, disposed = false, paymentMounted = false, paymentBusy = false;
  let fieldReadyTimer: ReturnType<typeof setTimeout> | undefined;
  let expiryTimer: ReturnType<typeof setInterval> | undefined;
  let payment: ReturnType<typeof sdk.createPaymentElement> | undefined;
  let express: ReturnType<typeof sdk.createExpressCheckoutElement> | undefined;
  const current = () => !disposed && options.isCurrent() && target.isConnected;
  const controller = createPaymentActions(loaded.actions, {
    update: (session, busy) => {
      if (!current()) return;
      paymentBusy = busy;
      const summary = describeCheckout(session);
      el('[data-payment-label]').textContent = busy ? 'Processing…' : session.recurring?.trial && session.total.total.minorUnitsAmount === 0 ? 'Start free trial' : 'Submit secure payment';
      submit.disabled = busy || !ready || !session.canConfirm;
      if (summaryTarget) {
        const set = (selector: string, value: string) => { summaryTarget.querySelector<HTMLElement>(selector)!.textContent = value; };
        set('[data-summary-plan]', session.recurring?.interval === 'year' ? 'Year-Plan' : session.recurring?.interval === 'week' && session.recurring?.intervalCount === 4 ? '4-Week plan' : session.recurring?.interval === 'week' ? '7-Day plan' : 'FocusCorp Premium');
        const comparison = !session.recurring?.trial ? getPriceComparison(session.currency, session.recurring?.interval ?? '', session.recurring?.intervalCount ?? 0, session.total.subtotal.minorUnitsAmount) : null;
        const euros = (amount: number) => new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(amount / 100);
        set('[data-summary-subtotal]', comparison ? euros(comparison.reference) : session.total.subtotal.amount);
        set('[data-summary-reference]', comparison?.label ?? '');
        summaryTarget.querySelector<HTMLElement>('[data-summary-offer-row]')!.hidden = !comparison;
        set('[data-summary-offer-label]', comparison ? `Offer saving (${comparison.percent}%)` : '');
        set('[data-summary-offer]', comparison ? `−${euros(comparison.saving)}` : '');
        set('[data-payment-total]', session.total.total.amount);
        set('[data-summary-terms]', summary.terms);
        const discount = session.total.discount;
        summaryTarget.querySelector<HTMLElement>('[data-summary-discount-row]')!.hidden = discount.minorUnitsAmount <= 0;
        const percent = session.discountAmounts?.length === 1 ? session.discountAmounts[0]?.percentOff : null;
        set('[data-summary-discount-label]', percent ? `Discount (−${percent}%)` : 'Discount');
        set('[data-summary-discount]', `−${discount.amount}`);
        const tax = session.total.taxExclusive;
        summaryTarget.querySelector<HTMLElement>('[data-summary-tax-row]')!.hidden = tax.minorUnitsAmount <= 0;
        set('[data-summary-tax]', tax.amount);
      }
      wallets.inert = busy;
    },
    error: message => { if (current()) el('[data-payment-error]').textContent = message; },
    submitted: options.submitted, failed: options.failed,
    expired: () => { cleanup(); options.fatal('This checkout session has expired. Please start a new one.'); },
    complete: options.complete,
  });
  const cleanup = () => {
    if (disposed) return;
    disposed = true;
    clearTimeout(fieldReadyTimer); clearInterval(expiryTimer);
    controller.destroy(); payment?.destroy(); express?.destroy(); target.replaceChildren();
  };
  const mountCard = () => {
    if (!current() || paymentMounted) return;
    paymentMounted = true;
    fieldReadyTimer = setTimeout(() => { if (current() && !ready) { cleanup(); options.fatal(PAYMENT_LOAD_ERROR); } }, 20_000);
    try { payment!.mount(el('[data-payment-element]')); }
    catch (error) { cleanup(); options.fatal(error instanceof Error ? error.message : PAYMENT_LOAD_ERROR); }
  };
  try {
    payment = sdk.createPaymentElement({
      wallets: { applePay: 'never', googlePay: 'never', link: 'never' }, layout: 'tabs',
      fields: { billingDetails: { name: 'always', address: 'never' } },
      terms: { card: 'never' },
    });
    express = sdk.createExpressCheckoutElement({
      buttonHeight: 52, buttonTheme: { applePay: 'black', googlePay: 'black' },
      buttonType: { applePay: 'subscribe', googlePay: 'subscribe' },
      paymentMethods: { applePay: 'always', googlePay: 'always', link: 'never', paypal: 'never', amazonPay: 'never', klarna: 'never' },
      paymentMethodOrder: ['apple_pay', 'google_pay'], layout: { maxColumns: 1, maxRows: 2, overflow: 'auto' },
    });
    payment.on('ready', () => { if (current()) { clearTimeout(fieldReadyTimer); ready = true; target.querySelector('[data-card-loading]')?.remove(); controller.update(); } });
    payment.on('loaderror', event => { if (current()) { cleanup(); options.fatal(event.error.message ?? PAYMENT_LOAD_ERROR); } });
    express.on('ready', event => {
      if (current()) wallets.classList.toggle('has-wallets', Boolean(event.availablePaymentMethods && Object.values(event.availablePaymentMethods).some(Boolean)));
    });
    // Checkout's type omits this documented Express Checkout event; reuse the
    // base Element listener type until Stripe adds the Checkout overload.
    (express as unknown as {
      on(event: 'availablepaymentmethodschange', handler: (event: StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent) => void): void;
    }).on('availablepaymentmethodschange', event => {
      if (current()) wallets.classList.toggle('has-wallets', Object.values(event.paymentMethods ?? {}).some(method => method?.available));
    });
    express.on('confirm', event => { if (current()) void controller.confirm(event); });
    sdk.on('change', () => { if (current()) controller.update(); });
    el<HTMLFormElement>('[data-payment-form]').addEventListener('submit', event => { event.preventDefault(); if (current() && !submit.disabled) void controller.confirm(); });
    controller.update();
    if (!current()) return { destroy: cleanup };
    express.mount(el('[data-express-element]'));
    mountCard();
    if (!current()) return { destroy: cleanup };
    options.ready();
    if (expiryTarget) {
      const refreshAt = getCheckoutRefreshDeadline(loaded.actions.getSession().id, options.expiresAt, sessionStorage);
      expiryTarget.hidden = false;
      const tick = () => {
        if (!current()) return;
        const remaining = checkoutSecondsRemaining(refreshAt);
        expiryTarget.querySelector<HTMLElement>('[data-checkout-countdown]')!.textContent = `${String(Math.floor(remaining / 60)).padStart(2, '0')}:${String(remaining % 60).padStart(2, '0')}`;
        // The visual timer does not end the offer. Only Stripe's actual session
        // expiry makes this payment session unusable.
        if (options.expiresAt && checkoutSecondsRemaining(options.expiresAt) === 0 && !paymentBusy) { cleanup(); options.fatal('This payment session expired. Please start a new checkout.'); }
      };
      expiryTimer = setInterval(tick, 1000); tick();
    }
  } catch (error) { cleanup(); throw error; }
  return { destroy: cleanup };
}
